import { useState, useRef } from "react";
import { Link } from "react-router-dom";
// --- swiper ----------------------------------------------------------------
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import "swiper/css";
// --- lottie ----------------------------------------------------------------
import Lottie from "lottie-react";
import contactLogo from "../../assets/lotties/logo-purple.json";
// --- media ----------------------------------------------------------------
import beforeVideo from "../../assets/videos/service-tune-before.mp4";
import afterVideo from "../../assets/videos/service-tune-after.mp4";
import {
  arrowIconRightG,
  kakaoIcon,
  linkIcon,
  serviceImg,
  soundWaveIcon,
  playIcon,
  pauseIcon,
  servicePortfolio04,
  servicePortfolio05,
  servicePortfolio06,
} from "../../assets/images";
// --- style ----------------------------------------------------------------
import "./Service.scss";

function SoundLab() {
  // --- 데이터 ----------------------------------------------------------------
  const soundStep = [
    {
      key: "arrangement",
      label: "편곡",
      title: "Arrangement",
      desc: "악기 배치 / 리듬 구성 / 장르 변환 / 오케스트레이션",
      note: "",
      price: "300,000원~",
    },
    {
      key: "recording",
      label: "보컬 녹음",
      title: "Recording",
      desc: "스튜디오 + 엔지니어 세션",
      note: "최소 2시간 기준",
      price: "70,000원",
      addNote: "보컬 디렉팅 포함",
      addNoteDesc: "1Pro 3시간",
      addNotePrice: "200,000원",
    },
    {
      key: "edit",
      label: "보컬 튠 · 에딧",
      title: "Tuning & Edit",
      desc: "피치 수정 / 타이밍 조정 / 감정 유지",
      note: "리비전 1회 포함",
      price: "100,000원",
    },
    {
      key: "mixing",
      label: "믹싱",
      title: "Mixing",
      desc: "트랙 밸런스 / 톤 정리 / 공간감 설계",
      note: "곡 10트랙 기준",
      price: "300,000원~",
    },
    {
      key: "mastering",
      label: "마스터링",
      title: "Mastering",
      desc: "DSP 최적화 / 음압 조절 / 최종 음질 보정",
      note: "곡 WAV + MP3 납품",
      price: "100,000원~",
    },
    {
      key: "sound",
      label: "사운드 디자인",
      title: "Sound Design",
      desc: "효과음 / 오프닝 사운드 / 브랜드 사운드 로고",
      note: "기업/유튜브용 가능",
      price: "120,000원~",
    },
    {
      key: "ai",
      label: "AI 음원 제작",
      title: "AI Music",
      desc: "로고송 / 배경음악 / 게임음악 / K-POP / 광고 BGM",
      note: "AI 기획부터 작곡 (1곡 기준)",
      price: "300,000원",
    },
    {
      key: "real",
      label: "음원 제작 \n(실제 작곡)",
      title: "실제 작곡",
      desc: "프로듀서 직접 작곡 / 멜로디 및 코드 구성",
      note: "저작권 보유형 선택 가능",
      price: "500,000원",
    },
  ];
  const [activeKey, setActiveKey] = useState(soundStep[0].key);
  const activeItem = soundStep.find((item) => item.key === activeKey) ?? soundStep[0];

  // --- 비디오 ----------------------------------------------------------------
  const videoRefs = useRef([]); // 0: before, 1: after
  const currentVideoRef = useRef(null); // 재생 중 video
  const [playingIdx, setPlayingIdx] = useState(null);

  // 프론트 코드 확인 필요
  const toggleVideo = async (idx) => {
    const video = videoRefs.current[idx];
    if (!video) return;

    // 다른 영상이 재생 중이면 중지
    if (currentVideoRef.current && currentVideoRef.current !== video) {
      currentVideoRef.current.pause();
      currentVideoRef.current.currentTime = 0;
    }

    if (video.paused) {
      await video.play();
      currentVideoRef.current = video;
      setPlayingIdx(idx);
    } else {
      video.pause();
      video.currentTime = 0;
      currentVideoRef.current = null;
      setPlayingIdx(null);
    }

    // 동영상 종료 시 아이콘 리셋
    video.onended = () => {
      currentVideoRef.current = null;
      setPlayingIdx(null);
    };
  };

  return (
    <div className="content-section soundlab">
      {/* === SOUND LAB DESCRIPTION STEP === */}
      <div className="soundlab__step max-12">
        <div className="step-bg">
          <div className="step-swiper-wrap">
            <Swiper
              className="step-swiper"
              slidesPerView="auto"
              freeMode
              spaceBetween={12}
              modules={[FreeMode]}
              preventClicks={true}
              touchStartPreventDefault={false}
              noSwiping={false}
            >
              {soundStep.map((item, idx) => (
                <SwiperSlide key={item.key} style={{ width: "auto" }}>
                  <button
                    type="button"
                    className={activeKey === item.key ? "active" : ""}
                    onClick={() => setActiveKey(item.key)}
                  >
                    {item.label}
                  </button>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
          <div className="step-content">
            <div className="content-bg">
              <h3 className="title">{activeItem.title}</h3>
              <p className="desc-01">{activeItem.desc}</p>

              <div>
                <p className="desc-02">
                  {activeItem.note && <span className="note">{activeItem.note}</span>}
                  <strong className="price">{activeItem.price}</strong>
                </p>
                {activeItem.addNote && (
                  <p className="desc-02">
                    <span className="note">{activeItem.addNote}</span>
                    <strong className="price">
                      <span>{activeItem.addNoteDesc}</span>
                      {activeItem.addNotePrice}
                    </strong>
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* === WORKFLOW === */}
      <section className="soundlab__flow max-12">
        <div className="content-section__title">
          <span>음악이 완성되는 정확한 5단계</span>
          <h3 className="gfont">MOB WORKFLOW</h3>
        </div>
        <ul>
          <li>
            <strong>01</strong>
            <p>파일 · 주문 접수</p>
          </li>
          <li>
            <img src={arrowIconRightG} alt="" aria-hidden="true" />
          </li>
          <li>
            <strong>02</strong>
            <p>
              작업 세팅 및 기획
              <span>(엔지니어 · 아티스트 매칭)</span>
            </p>
          </li>
          <li>
            <img src={arrowIconRightG} alt="" aria-hidden="true" />
          </li>
          <li className="point">
            <strong>03</strong>
            <p>
              중간 피드백
              <span>(1차 샘플 전달)</span>
            </p>
          </li>
          <li>
            <img src={arrowIconRightG} alt="" aria-hidden="true" />
          </li>
          <li>
            <strong>04</strong>
            <p>
              Revision
              <span>(수정 요청 반영)</span>
            </p>
          </li>
          <li>
            <img src={arrowIconRightG} alt="" aria-hidden="true" />
          </li>
          <li>
            <strong>05</strong>
            <p>
              최종 납품
              <span>(WAV / MP3 / 리포트)</span>
            </p>
          </li>
        </ul>
      </section>

      {/* === MOB IMPACT === */}
      <section className="soundlab__impact">
        <div className="content-section__title dark max-12">
          <span>MOB 서비스를 통해 음악이 어떻게 달라지는지 확인해 보세요</span>
          <h3 className="gfont">MOB IMPACT</h3>
        </div>

        <ul>
          <li>
            <h4>ORIGINAL</h4>
            <div className="video-box">
              <video
                ref={(el) => (videoRefs.current[0] = el)}
                src={beforeVideo}
                preload="metadata"
                playsInline // 강제 전체 화면 금지
              ></video>
              <button
                type="button"
                className="play-btn"
                aria-label={playingIdx === 0 ? "Pause video" : "Play video"}
                onClick={() => toggleVideo(0)}
              >
                <img src={playingIdx === 0 ? pauseIcon : playIcon} alt="" aria-hidden="true" />
              </button>
            </div>
          </li>
          <li>
            <img src={serviceImg} alt="" aria-hidden="true" />
            <p>
              <img src={soundWaveIcon} alt="" aria-hidden="true" />
              AI 음향 기술이 적용되고 있습니다.
            </p>
          </li>
          <li>
            <h4>MOB IMPACT</h4>
            <div className="video-box">
              <video ref={(el) => (videoRefs.current[1] = el)} src={afterVideo} preload="metadata" playsInline></video>
              <button
                type="button"
                className="play-btn"
                aria-label={playingIdx === 1 ? "Pause video" : "Play video"}
                onClick={() => toggleVideo(1)}
              >
                <img src={playingIdx === 1 ? pauseIcon : playIcon} alt="" aria-hidden="true" />
              </button>
            </div>
          </li>
        </ul>
      </section>

      {/* === PORTFOLIO === */}
      <section className="soundlab__portfolio max-12">
        <div className="content-section__title">
          <span>MOB studio가 만들어온 다양한 음악과 프로젝트를 소개합니다</span>
          <h3 className="gfont">PORTFOLIO</h3>
        </div>

        <ul>
          <li>
            <Link
              to="/service"
              state={{ tabKey: "sound" }}
              className="scale-wrap"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <span className="scale-bg" style={{ backgroundImage: `url(${servicePortfolio04})` }} aria-hidden="true" />
              <h4>
                <span>편곡 · 작곡</span>
                <img src={linkIcon} alt="" aria-hidden="true" />
              </h4>
            </Link>
          </li>
          <li>
            <Link to="/music" className="scale-wrap">
              <span className="scale-bg" style={{ backgroundImage: `url(${servicePortfolio05})` }} aria-hidden="true" />
              <h4>
                <span>음원 제작</span>
                <img src={linkIcon} alt="" aria-hidden="true" />
              </h4>
            </Link>
          </li>
          <li>
            <Link to="/portfolio" className="scale-wrap">
              <span className="scale-bg" style={{ backgroundImage: `url(${servicePortfolio06})` }} aria-hidden="true" />
              <h4>
                <span>엔지니어 · 작곡가</span>
                <img src={linkIcon} alt="" aria-hidden="true" />
              </h4>
            </Link>
          </li>
        </ul>
      </section>

      {/* === RECORDING STUDIO BANNER === */}
      <section className="mob-live-studio">
        <div className="content-section__title max-12">
          <span>당신의 목소리가 음악이 되는 순간</span>
          <h3 className="gfont">RECORDING STUDIO</h3>
        </div>
        <div className="studio-content recording">
          <h4>프로의 기술로 공간 사운드를 완성합니다.</h4>
          <p>
            MOB STUDIO는 기술과 감성의 조화를 <br />
            당신의 음악 세계로 보냅니다
          </p>
        </div>
      </section>

      {/* === INQUIRY === */}
      <div className="content-section__contact">
        <p className="desc">ALL MAKERS, ONE STAGE. 지금 당신의 무대를 시작하세요</p>
        <h3 className="gfont title">RESERVATION INQUIRY</h3>
        <Lottie animationData={contactLogo} loop autoplay className="contact-lottie" />

        <div className="kakao-link">
          <span>지금 시작해 보세요.</span>
          <a href="https://pf.kakao.com/_QLTxgn" target="_blank" rel="noopener noreferrer">
            <img src={kakaoIcon} alt="" aria-hidden="true" />
            카카오 채널 문의
          </a>
        </div>
      </div>
    </div>
  );
}

export default SoundLab;
